/**
 * FileGridView Component
 * Grid view for displaying files with thumbnails
 */

import React from 'react';
import { Grid, Center } from '@/components/ui/layout';
import { FileCard, FolderCard, FileEmptyState } from '@/components/ccms-components/file-management';
import { Loader } from '@/components/ui/feedback';
import { IconFolder } from '@tabler/icons-react';
import { FileRecordModelView, FolderInfo } from '../types';

interface FileGridViewProps {
  files: FileRecordModelView[];
  folders: FolderInfo[];
  selectedFileIds: Set<string>;
  onFileSelect: (fileId: string) => void;
  onFileClick: (file: FileRecordModelView) => void;
  onFolderClick: (folder: FolderInfo) => void;
  onDownload: (fileId: string, fileName: string) => void;
  onPreview: (file: FileRecordModelView) => void;
  onDelete: (fileId: string) => void;
  loading?: boolean;
}

export const FileGridView: React.FC<FileGridViewProps> = ({
  files,
  folders,
  selectedFileIds,
  onFileSelect,
  onFileClick,
  onFolderClick,
  onDownload,
  onPreview,
  onDelete,
  loading = false,
}) => {
  const formatFileSize = (bytes: number): string => {
    const sizes = ['B', 'KB', 'MB', 'GB'];
    if (bytes === 0) return '0 B';
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return `${(bytes / Math.pow(1024, i)).toFixed(2)} ${sizes[i]}`;
  };

  const getFileIcon = (mimeType: string) => {
    if (mimeType.startsWith('image/')) return '🖼️';
    if (mimeType.startsWith('video/')) return '🎥';
    if (mimeType.startsWith('audio/')) return '🎵';
    if (mimeType.includes('pdf')) return '📄';
    if (mimeType.includes('zip') || mimeType.includes('archive')) return '📦';
    return '📎';
  };

  if (loading) {
    return <Center p="xl"><Loader /></Center>;
  }

  if (folders.length === 0 && files.length === 0) {
    return (
      <FileEmptyState
        message="No files or folders"
        icon={IconFolder}
        action="Upload Files"
        onActionClick={() => {/* Handle upload */}}
      />
    );
  }

  return (
    <Grid
      gutter="md"
    >
      {/* Render folders first using FolderCard component */}
      {folders.map((folder) => (
        <Grid.Col key={folder.id} span={{ base: 12, sm: 6, md: 4, lg: 3 }}>
          <FolderCard
            folder={folder}
            onClick={() => onFolderClick(folder)}
            hoverEffect="lift"
            gradientVariant="primary"
          />
        </Grid.Col>
      ))}

      {/* Render files using FileCard component */}
      {files.map((file) => (
        <Grid.Col key={file.id} span={{ base: 12, sm: 6, md: 4, lg: 3 }}>
          <FileCard
            file={{
              id: file.id,
              fileName: file.fileName,
              mimeType: file.mimeType,
              fileSize: file.fileSize,
              url: file.url,
              status: file.status,
              uploadedAt: file.createdAt,
              uploadedBy: file.userFirstName ? `${file.userFirstName} ${file.userLastName}` : (file.userEmail || undefined),
            }}
            onPreview={() => onPreview(file)}
            onDownload={() => onDownload(file.id, file.fileName)}
          onDelete={() => onDelete(file.id)}
          variant="detailed"
          gradientVariant="primary"
          showActions
        />
        </Grid.Col>
      ))}
    </Grid>
  );
};
